// The ONE place a lot's effective Sales Rep is resolved (BUG-1100).
//
// A lot's rep is a two-rung cascade: the lot's own override wins, and the
// seller-customer's assigned rep is the inherit. Before BUG-1100 that rule was
// re-implemented at five call sites — the inventory/Items list projection, the
// clerk-catalog expand, the rings mapper, the lot-detail service, and the
// settlement SQL aggregation — which drifted into THREE different answers for
// "nobody is assigned": the literal word 'House', an empty cell, and
// 'Unassigned'. Same failure shape ADR 045 records for seller commission and
// ADR 032 for buyer's premium; same fix — one resolver, one sentinel.
//
// This is an IsomorphicService (ADR 010): pure, no I/O, client-safe, and
// generic on the rung shape so a raw column read and a slim client projection
// both satisfy it without a cast.

import { formatSalesRepName, type SalesRepNameInput } from '@auctogon/core/sales-rep';

/**
 * One rung of the cascade: the FK id plus the joined roster row's display parts.
 *
 * `id` and `rep` are carried separately and BOTH matter. Presence keys on `id`
 * alone — a roster row may legitimately carry no name parts at all (BL-877
 * renders it 'Unnamed rep'), so a name-based presence test would treat a real
 * assignment as absent and silently fall through to the next rung.
 */
export interface SalesRepRung {
  /** The rung's FK value — `auction_lots.sales_rep_id` or `customers.sales_rep_id`. Null = unset. */
  id: string | null;
  /** The joined `sales_rep` row's display parts. Null when the join found nothing. */
  rep: SalesRepNameInput | null;
}

/** What every rep-rendering surface needs: which rep won, and what to print. */
export interface EffectiveSalesRep {
  /** The winning rung's roster id, or null when neither rung is set. */
  repId: string | null;
  /**
   * The string to render. Never null — `HOUSE_SALES_REP_LABEL` when no rung
   * resolves, so a caller cannot reintroduce a per-surface empty state by
   * forgetting a `?? 'House'`.
   */
  displayName: string;
  /** Which rung won — useful for hints ("Sales rep for seller differs"). */
  source: 'lot' | 'seller' | 'house';
}

/**
 * What every surface renders when no rep resolves on either rung.
 *
 * Deliberately NOT pushed down into `formatSalesRepName`: that helper's contract
 * is "format a roster row", and it returns null for "there is no row" so callers
 * can still distinguish absence. The sentinel is a resolution concern, and it
 * lives here so there is exactly one of it.
 */
export const HOUSE_SALES_REP_LABEL = 'House';

/**
 * The Sales Rep FILTER's "no rep" option value (BL-752 on Customers, BL-1610 on
 * Inventory) — the literal a list route receives as `?salesRepId=` when the
 * operator picks House, and the value a repository's rep predicate turns into
 * `<rep id expression> IS NULL`.
 *
 * Declared beside the display label because the two are the same word by
 * design (the option reads "House" and means "House"), yet they are different
 * jobs: this one is a wire sentinel, that one is what a cell renders. Reading
 * one from the other at a call site hides which job is being done.
 */
export const SALES_REP_FILTER_HOUSE = 'House';

/**
 * Resolve a lot's effective Sales Rep from its two rungs, most-specific first.
 *
 * **Presence-wins on the FK id.** A lot-level override that is set stops the
 * walk even if its roster row failed to join — falling through to the seller in
 * that case would render a *different real person's* name for an assignment the
 * operator explicitly made, which is the silent-wrong-attribution failure
 * BUG-1100 exists to close. A set-but-unjoined override renders 'Unnamed rep'
 * via the formatter instead.
 *
 * **Archived reps still resolve**, suffixed '(Archived)' by the formatter — the
 * ADR 018 §6 display carve-out. Callers must NOT filter `deleted_at` on the
 * roster join, and must not filter it on the seller-customer join either
 * (BL-880 AC 11: an archived seller's rep must not collapse to House).
 *
 * Pure — no I/O, no side effects.
 *
 * @param rungs - The lot's own override rung and the seller-customer's rung.
 * @returns The winning rep's id, the string to render, and which rung won.
 */
export function resolveEffectiveSalesRep(rungs: {
  /** `auction_lots.sales_rep_id` + its joined roster row, for THIS lot. */
  lotOverride: SalesRepRung | null | undefined;
  /** `customers.sales_rep_id` + its joined roster row, for the lot's seller. */
  sellerRep: SalesRepRung | null | undefined;
}): EffectiveSalesRep {
  const { lotOverride, sellerRep } = rungs;

  if (lotOverride?.id != null) {
    return {
      repId: lotOverride.id,
      displayName: formatSalesRepName(lotOverride.rep) ?? HOUSE_SALES_REP_LABEL,
      source: 'lot',
    };
  }

  if (sellerRep?.id != null) {
    return {
      repId: sellerRep.id,
      displayName: formatSalesRepName(sellerRep.rep) ?? HOUSE_SALES_REP_LABEL,
      source: 'seller',
    };
  }

  return { repId: null, displayName: HOUSE_SALES_REP_LABEL, source: 'house' };
}
